import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { X, Zap, ArrowRight } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export interface UpgradePromptProps {
  isOpen: boolean;
  onClose: () => void;
  feature?: string;
  reason?: string;
  targetPlan?: 'Pro' | 'Enterprise';
  dismissible?: boolean;
}

export function UpgradePrompt({
  isOpen,
  onClose,
  feature,
  reason,
  targetPlan = 'Pro',
  dismissible = true,
}: UpgradePromptProps): React.JSX.Element {
  const planFeatures = {
    Pro: [
      'Unlimited repositories',
      'Advanced code analysis',
      'PRD + decision logging',
      'Priority support',
      '5 workspaces',
      'Team collaboration',
      'API access',
    ],
    Enterprise: [
      'Everything in Pro',
      'Unlimited workspaces',
      'SSO authentication',
      'Custom integrations',
      'Dedicated support',
      'SLA guarantees',
      'On-premise option',
    ],
  };

  const planPrice = targetPlan === 'Pro' ? 29 : 99;

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/60">
                <Zap className="h-5 w-5 text-primary-foreground" />
              </div>
              <div>
                <AlertDialogTitle className="text-xl">
                  Upgrade to {targetPlan}
                </AlertDialogTitle>
                <Badge variant="secondary" className="mt-1">
                  ${planPrice}/month
                </Badge>
              </div>
            </div>
            {dismissible && (
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          {(feature || reason) && (
            <AlertDialogDescription className="pt-2">
              {reason || `${feature} is available on the ${targetPlan} plan.`}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>

        <div className="py-4">
          <h4 className="font-semibold mb-3">What you get with {targetPlan}:</h4>
          <ul className="space-y-2">
            {planFeatures[targetPlan].map((feature) => (
              <li key={feature} className="flex items-center gap-2 text-sm">
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10">
                  <ArrowRight className="h-3 w-3 text-primary" />
                </div>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          {dismissible && (
            <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Not Now
            </Button>
          )}
          <Button asChild className="w-full sm:w-auto">
            <Link to="/pricing">
              View Pricing
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Banner variant for inline upgrade prompts
 */
export interface UpgradeBannerProps {
  feature?: string;
  targetPlan?: 'Pro' | 'Enterprise';
  onDismiss?: () => void;
}

export function UpgradeBanner({
  feature,
  targetPlan = 'Pro',
  onDismiss,
}: UpgradeBannerProps): React.JSX.Element {
  return (
    <div className="relative rounded-lg border border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10 p-4">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/60 flex-shrink-0">
          <Zap className="h-5 w-5 text-primary-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold mb-1">Upgrade to {targetPlan}</h3>
          <p className="text-sm text-muted-foreground mb-3">
            {feature
              ? `${feature} is available on the ${targetPlan} plan.`
              : `Unlock advanced features with ${targetPlan}.`}
          </p>
          <Button asChild size="sm">
            <Link to="/pricing">
              View Pricing
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
        </div>
        {onDismiss && (
          <Button variant="ghost" size="sm" onClick={onDismiss} className="flex-shrink-0">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
